const PAGE_WRAPPER_SELECTOR = '.page-wrapper';
const SQUEEZE_QUERY = window.matchMedia('(min-width: 1024px)');

/**
 * Returns the current page scroll container.
 * In the squeeze layout (desktop ≥1024px), `.page-wrapper` is the scroll container instead of
 * `document.scrollingElement`. On mobile, the document root scrolls natively so the address bar
 * can hide/show.
 *
 * @returns {Element} The scroll container element
 */
function getScrollContainer() {
  if (SQUEEZE_QUERY.matches) {
    return document.querySelector(PAGE_WRAPPER_SELECTOR) ?? document.scrollingElement ?? document.documentElement;
  }
  return document.scrollingElement ?? document.documentElement;
}

/**
 * Returns the current scroll position of the page scroll container.
 *
 * @returns {number} The scrollTop value
 */
function getScrollTop() {
  return getScrollContainer().scrollTop;
}

/**
 * Scrolls the page scroll container to the specified position.
 *
 * @param {ScrollToOptions} options - The scroll options (top, left, behavior)
 */
function scrollTo(options) {
  getScrollContainer().scrollTo(options);
}

/**
 * Manual scroll restoration for `.page-wrapper` as scroll container.
 *
 * On desktop (≥1024px), the browser restores scroll on `document.scrollingElement`, which
 * has `overflow: hidden` in the squeeze layout and can never scroll. We disable native
 * restoration and handle it via `history.state` instead.
 *
 * On mobile (<1024px), `document.scrollingElement` IS the scroll container, so native
 * restoration works perfectly — leave it enabled to avoid a one-frame flash from rAF.
 *
 * Save: `pagehide` — fires exactly once per navigation, captures the exact scroll position
 * at the moment the user leaves. More accurate than debounced scroll (which can be stale
 * if the user scrolls and immediately clicks a link).
 *
 * Restore: unconditional `pageshow` — Horizon uses cross-document view transitions, so
 * `pageshow` fires on every navigation (both bfcache and fresh loads). `popstate` is not
 * used because it doesn't fire for cross-document back navigation.
 */
if (SQUEEZE_QUERY.matches) {
  history.scrollRestoration = 'manual';
}

SQUEEZE_QUERY.addEventListener('change', () => {
  history.scrollRestoration = SQUEEZE_QUERY.matches ? 'manual' : 'auto';
});

/** How long a restore keeps retrying while the page is still streaming in. */
const RESTORE_TIMEOUT_MS = 1500;

/**
 * True while a restore is re-applying, so its own scrolls are not saved back.
 *
 * Starts true: on a back navigation the browser performs its own scroll restoration before this
 * module's `pageshow` handler runs, and it clamps against a page that has not finished streaming.
 * Measured coming back to a PLP saved at 900: the browser landed on 353 at t=352ms and `scrollend`
 * wrote that 353 over the saved 900, so the restore at t=468ms then had nothing better to apply.
 * Saving stays blocked until the restore below has settled.
 */
let isRestoringScroll = true;

/**
 * Saves the current scroll position into the current history entry.
 */
function saveScrollPosition() {
  try {
    const currentState = typeof history.state === 'object' && history.state !== null ? history.state : {};
    history.replaceState({ ...currentState, scrollTop: getScrollContainer().scrollTop }, '');
  } catch (_) {
    // replaceState can throw if the state object exceeds the browser's size limit
  }
}

window.addEventListener('pagehide', saveScrollPosition);

/**
 * Keep the saved position in step with where the page actually is, not just where it was the
 * last time the page was left.
 *
 * `pagehide` alone stranded a stale value. Scrolling back to the top does not navigate, so
 * nothing rewrote `history.state.scrollTop` — it still held the offset saved when the page was
 * last left (e.g. 900 from clicking into a product). Reloading from the top then restored 900 and
 * dropped the shopper into the middle of the grid. `pagehide` does fire on reload and does save
 * 0, but too late: the browser has already snapshotted the entry's state for the new document.
 *
 * `scrollend` fires once per gesture, so it costs one write per scroll rather than one per frame.
 * Where it is unsupported a debounce stands in — 500ms keeps this clear of Safari's ~100
 * replaceState calls per 30s. Both paths skip the write when the value has not moved.
 */
let pendingScrollSave = /** @type {ReturnType<typeof setTimeout> | null} */ (null);
let lastSavedScrollTop = /** @type {number | null} */ (null);

function saveScrollPositionIfMoved() {
  if (!SQUEEZE_QUERY.matches) return;

  // `scrollend` fires for programmatic scrolls too, so without this the restore's own intermediate
  // (still-clamped) offsets would be written back as the saved position and become sticky.
  if (isRestoringScroll) return;

  const scrollTop = getScrollContainer().scrollTop;
  if (scrollTop === lastSavedScrollTop) return;

  lastSavedScrollTop = scrollTop;
  saveScrollPosition();
}

if ('onscrollend' in window) {
  // Capture: scroll events do not bubble, but they do reach ancestors in the capture phase, and
  // the scroll container is `.page-wrapper` on desktop and the document on mobile.
  document.addEventListener('scrollend', saveScrollPositionIfMoved, { capture: true, passive: true });
} else {
  document.addEventListener(
    'scroll',
    () => {
      if (pendingScrollSave) clearTimeout(pendingScrollSave);
      pendingScrollSave = setTimeout(() => {
        pendingScrollSave = null;
        saveScrollPositionIfMoved();
      }, 500);
    },
    { capture: true, passive: true }
  );
}

/**
 * Restores a saved scroll position onto the current scroll container.
 *
 * @param {number} savedScrollTop
 */
function restoreSavedScrollTop(savedScrollTop) {
  if (!Number.isFinite(savedScrollTop) || savedScrollTop < 0) return;

  const container = getScrollContainer();

  // One rAF is too early on a collection page: ~1MB of HTML is still streaming, so the container
  // has not reached its full height and `Math.min(saved, max)` clamped the target to whatever had
  // rendered — coming back to a PLP scrolled to 900 landed at 353. Keep re-applying until the
  // container is tall enough to honour the saved offset, or until the deadline, so a page that
  // legitimately got shorter still settles instead of spinning.
  const deadline = performance.now() + RESTORE_TIMEOUT_MS;

  isRestoringScroll = true;

  const attempt = () => {
    const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);

    // Use scrollTo with 'instant' to override CSS scroll-behavior: smooth on .page-wrapper
    container.scrollTo({ top: Math.min(savedScrollTop, maxScrollTop), behavior: 'instant' });

    if (maxScrollTop < savedScrollTop && performance.now() < deadline) {
      requestAnimationFrame(attempt);
      return;
    }

    isRestoringScroll = false;
  };

  attempt();
}

/**
 * Applies the position held in the current history entry, if it has one.
 *
 * @param {{ defer?: boolean }} [options] - `defer: false` applies it in this task rather than on
 *   the next frame, so the page can be placed before its first paint.
 */
function restoreFromHistoryState({ defer = true } = {}) {
  const scrollTop = history.state?.scrollTop;

  // Nothing to restore: release the save block so ordinary scrolling is recorded again.
  if (scrollTop == null) {
    isRestoringScroll = false;
    return;
  }

  if (!defer) {
    restoreSavedScrollTop(scrollTop);
    return;
  }

  requestAnimationFrame(() => {
    restoreSavedScrollTop(scrollTop);
  });
}

/**
 * A cross-document back navigation re-runs this module as an ordinary deferred module script,
 * which is the earliest the position can be applied — the document is parsed, so the container
 * exists and already has most of its height.
 *
 * `pageshow` is far too late for that case: it waits on `load`, i.e. every product image on the
 * grid. Measured coming back to a PLP saved at 2400, the page painted at the top and stayed there
 * for 1.4s before snapping down — the visible "jump" at the product cards. `restoreSavedScrollTop`
 * re-applies until the container is tall enough, so running it here costs nothing when the grid is
 * still streaming — and applying it in this task rather than on the next frame gets it in ahead of
 * the first paint.
 */
restoreFromHistoryState({ defer: false });

window.addEventListener('pageshow', (event) => {
  // A bfcache restore does not re-execute the module, so that path still needs the event. A fresh
  // load has already been handled above, and redoing it here would yank a shopper who has started
  // scrolling in the meantime.
  if (!event.persisted) return;

  restoreFromHistoryState();
});

/**
 * Same-document scroll restoration for pushState navigation.
 *
 * `pagehide`/`pageshow` only fire for cross-document navigations. For same-document
 * pushState navigation (filters, pagination), we patch pushState to auto-save scroll
 * position before each new entry, and restore on popstate (back/forward).
 */
const originalPushState = history.pushState.bind(history);
history.pushState = function (state, title, url) {
  // Not while restoring. A filtered PLP pushes state during load (the facets component
  // normalises the URL), which otherwise wrote the browser's early clamped offset over the
  // position actually being restored — coming back to a grid saved at 900 stuck at 353.
  if (!isRestoringScroll) saveScrollPosition();

  originalPushState(state, title, url);
};

window.addEventListener('popstate', () => {
  const scrollTop = history.state?.scrollTop;
  if (scrollTop == null) return;

  requestAnimationFrame(() => {
    restoreSavedScrollTop(scrollTop);
  });
});

/**
 * Returns the appropriate target for listening to scroll events.
 * On desktop (≥1024px), `.page-wrapper` emits scroll events directly.
 * On mobile, the document root scrolls natively and scroll events bubble to `document`.
 *
 * @returns {EventTarget} The target to call addEventListener('scroll', ...) on
 */
function getScrollEventTarget() {
  if (SQUEEZE_QUERY.matches) {
    return document.querySelector(PAGE_WRAPPER_SELECTOR) ?? document;
  }
  return document;
}

/**
 * Returns the appropriate root for an IntersectionObserver monitoring the scroll container.
 * On desktop (≥1024px), `.page-wrapper` must be set as the explicit root.
 * On mobile, `null` uses the viewport root (the IntersectionObserver default).
 *
 * @returns {Element | null} The root option for IntersectionObserver
 */
function getIntersectionRoot() {
  if (SQUEEZE_QUERY.matches) {
    return document.querySelector(PAGE_WRAPPER_SELECTOR) ?? null;
  }
  return null;
}

export {
  getScrollContainer,
  getScrollTop,
  scrollTo,
  getScrollEventTarget,
  getIntersectionRoot,
  SQUEEZE_QUERY as scrollContainerMediaQuery,
};
